// خطِ لوله از دیدِ **مرحله‌ها** — هر مرحله چه کار می‌کند، چقدر کار کرده،
// چقدر خطا خورده، خطا از چه جنسی بوده، و آیتم‌هایی که وارد آن مرحله شدند
// سرِ آخر کجا رفتند.
//
// ═══ چرا نمودارِ سوم، در حالی که دو تا داریم ═══
//
// دو نمودارِ موجود هر کدام یک **واحد** را دنبال می‌کنند و در این کار خوب‌اند:
// «سرنوشتِ درخواست‌ها» یک آدرس را دنبال می‌کند و «جریانِ خطِ لوله» یک
// رویدادِ نامزد را. ولی هیچ‌کدام به این سوال جواب نمی‌دهند: **خودِ مرحله‌ها
// چطور کار می‌کنند؟** سنکی جریانِ آیتم را می‌کشد، نه سلامتِ ماشینی که آیتم
// از تویش رد می‌شود. اینکه فازِ `match` در ده روزِ اخیر ۴۰ بار دویده و ۶ بارش
// شکسته و آخرین خطایش تایم‌اوت بوده، در هیچ‌کدام از آن دو نمودار جایی ندارد
// — در جدولِ «فازها» هست، ولی آن‌جا از جریانِ آیتم‌ها جداست و آدم باید در
// ذهنش دو چیز را کنارِ هم بگذارد.
//
// این فایل همان دو تا را کنارِ هم می‌گذارد: برای هر مرحله، هم شمارشِ آیتم‌ها
// (از `admin_crawler_funnel`) و هم کارنامه‌ی اجراها (از `admin_crawler_stages`).
//
// ═══ قاعده‌ای که همه‌جای این فایل رعایت می‌شود ═══
//
// ⚠ **«سرنوشت‌ها» همیشه ورودی را تقسیم می‌کنند، نه خروجی را.** یعنی جمعِ
// سرنوشت‌های غیرِ `aside` باید دقیقاً ورودیِ همان مرحله بشود. این قاعده تنها
// چیزی است که جلوی خطای بی‌صدا را می‌گیرد: نموداری که ستونش از خودش بزرگ‌تر
// شود هیچ‌جا قرمز نمی‌شود، فقط عددی نشان می‌دهد که آدم به آن اعتماد می‌کند.
// آن‌چه ته نکشد در `residual` می‌نشیند و رابط صریح «حساب‌نشده» می‌نویسدش.
//
// ⚠ و **ورودی و خروجیِ یک مرحله اغلب هم‌واحد نیستند.** یک سند ده‌ها کاندیدا
// می‌دهد. این پنهان نمی‌شود، `unitShift` صریح می‌گویدش — همان اشتباهی که یک
// بار گرهِ «سندِ خام» را ۳۰٬۱۶۷ نشان داد در حالی که سندها ۴٬۵۷۴ تا بودند
// (سرِ `crawlerFlow.ts`).
//
// خالص و بی‌ایمپورت، پس با `npm test` آزمودنی است. و آزمودنش مهم است: هر سه
// عددِ این صفحه از جای دیگری هم گفته شده‌اند و اگر این‌جا جور دیگری جمع بزنند،
// دو بخشِ یک صفحه دو حرف می‌زنند.
//
// تاکسونومیِ مرحله‌ها اختراع نشده: نامِ فازها از
// `event_crawler/ARCHITECTURE-VNEXT.md` می‌آید — همان زنجیره‌ای که سرِ
// `CrawlerFunnel` در `opsRepo.ts` نوشته شده.

/** یک عدد، دو بار — همان قراردادِ `Counts`، ولی بدونِ ایمپورت. */
export interface Pair {
  /** در ۱۰ روزِ گذشته. */
  d10: number;
  /** در آخرین اجرای کاملِ خطِ لوله. */
  run: number;
}

/**
 * کلیدهایی از `CrawlerFunnel` که این گزارش می‌خواند.
 *
 * ⚠ همه‌شان اختیاری‌اند و این عمدی است: صفحه از کشِ `crawler.admin_reports`
 * می‌خواند و آن را کرالر می‌سازد. کلیدی که هنوز در کش نیست باید «۰» بشود و
 * مرحله‌اش خالی دیده شود، نه اینکه کلِ صفحه با `undefined.d10` سفید شود.
 */
export type FunnelKey =
  // ⚠ خانواده‌ی مایگریشنِ ۰۱۴۴ — سه مرحله‌ی **قبل از** گرفتنِ صفحه. تا آن
  // مایگریشن نمودار از «گرفتنِ صفحه» شروع می‌شد، انگار فهرستِ منابع از آسمان
  // آمده؛ نیامده، کرالر خودش هم منبع پیدا می‌کند.
  | 'disc_runs' | 'disc_candidates'
  | 'insp_total' | 'insp_usable' | 'insp_blocked' | 'insp_unusable'
  | 'drafts_total' | 'drafts_promoted' | 'drafts_rejected' | 'drafts_pending'
  | 'sources_total' | 'sources_enabled' | 'sources_disabled'
  | 'fetch_requests' | 'fetch_ok' | 'fetch_unchanged' | 'fetch_errors'
  | 'fetch_blocked' | 'fetch_skipped'
  | 'fetch_blocked_robots' | 'fetch_blocked_tier' | 'fetch_blocked_disabled'
  | 'docs_listing' | 'docs_detail' | 'detect_documents' | 'detect_barren'
  | 'candidates' | 'cand_claims_run' | 'cand_claims_none'
  | 'cand_expand_attached' | 'cand_no_mention' | 'claims'
  // ۰۱۴۴ — دلیلِ «در صفِ کار»، که تا آن موقع از تفریق در می‌آمد.
  // ⚠ هر چهارتا با زمانِ **خودِ کاندیدا** پنجره می‌خورند و برای همین دقیقاً
  // `candidates` می‌شوند. `cand_insufficient` عمداً جدا از `cand_no_mention`
  // است: آن یکی پنجره‌اش زمانِ جدولِ برآیند است و نمودارِ سنکی رویش بنا شده.
  | 'cand_built' | 'cand_insufficient'
  | 'cand_pending_no_build' | 'cand_pending_no_extract'
  | 'mentions' | 'clusters' | 'clusters_multi_source'
  | 'match_same' | 'match_related' | 'match_uncertain' | 'match_different'
  | 'events_dead_cluster' | 'events_no_url' | 'events_no_date'
  | 'policy_publish' | 'policy_review' | 'policy_reject'
  // ۰۱۴۴ — «نرفته» در واقع دو چیزِ خیلی متفاوت بود.
  | 'notsent_in_app' | 'notsent_gone'
  | 'publishable_mapped' | 'published_live' | 'orphaned';

export type StageCounts = Partial<Record<FunnelKey, number>>;

/**
 * چیزهایی که شکلِ `Counts` ندارند و در همان پیلودِ قیف می‌آیند (۰۱۴۴).
 *
 * جدا از `StageCounts` است چون آرایه‌اند و دو ستونِ ده‌روزه/آخرین‌اجرا
 * ندارند: هر سه از پنجره‌ی **ده روز** می‌آیند و همین‌جا نوشته می‌شود تا
 * کسی دنبالِ ستونِ دومشان نگردد.
 */
export interface StageExtras {
  fetch_repeat?: { bucket: string; requests: number; urls: number; sources: number }[];
  drafts_reasons?: { reason: string; n: number }[];
  policy_reasons?: { decision: string; reason: string; n: number }[];
  notsent_rows?: {
    title: string; url: string | null; source_id: string | null;
    why: string; at: string;
  }[];
}

/**
 * یک ردیفِ `admin_crawler_stages`، فقط آن‌قدری که این‌جا لازم است.
 *
 * عمداً زیرمجموعه‌ی `CrawlerStage` است و نه خودش: این فایل نباید به
 * `data/opsRepo` وابسته شود، وگرنه دیگر خالص نیست.
 */
export interface StageRun {
  stage: string;
  runs: Pair;
  failures: Pair;
  p50_seconds?: { d10: number | null; run: number | null } | null;
  p95_seconds?: { d10: number | null; run: number | null } | null;
  last_run?: string | null;
  last_ok?: string | null;
  last_error?: string | null;
}

/**
 * سرنوشتِ یک دسته از آیتم‌های ورودیِ یک مرحله.
 *
 * چهار جنس دارد و رنگشان در رابط فرق می‌کند، چون سوالِ آدم «چقدر رد شد»
 * نیست، «کجا ریخت» است:
 *   `pass` رفت به مرحله‌ی بعد.
 *   `drop` همین‌جا تمام شد.
 *   `hold` نه رفت نه ریخت — منتظر است یا دستِ آدم.
 *   `info` نه ریزش است نه پیشرفت؛ فقط باید دانسته شود.
 */
export type FateKind = 'pass' | 'drop' | 'hold' | 'info';

export interface Fate {
  id: string;
  label: string;
  kind: FateKind;
  v: Pair;
  /** چه شد، به زبانِ ساده. */
  why: string;
  /** همان، با یک مثالِ دنیای واقعی. */
  like?: string;
  /** سهمش از ورودیِ مرحله، بر حسبِ درصدِ ستونِ ده‌روزه. `null` یعنی ورودی صفر است. */
  share: number | null;
  /**
   * ⚠ این عدد ورودیِ مرحله را **تقسیم نمی‌کند** و در `residual` هم حساب
   * نمی‌شود. برای شمارش‌هایی است که از جدولِ دیگری می‌آیند و واحدشان با
   * ورودی یکی نیست — مثلِ «رویدادِ بدونِ تاریخ» که روی `events` شمرده
   * می‌شود، نه روی حکم‌های `publish`.
   */
  aside?: boolean;
}

/**
 * جنسِ خطای یک فاز، از روی متنِ آخرین خطایش.
 *
 * ⚠ **حدس است و رابط باید همین را بگوید.** دیتابیس فقط یک رشته‌ی خطا
 * نگه می‌دارد و دسته‌ای برایش ندارد؛ این‌جا از روی الگوی متن دسته‌بندی
 * می‌شود. اشتباهش بی‌ضرر است (یک برچسبِ غلط)، ولی وانمود کردن به اینکه
 * دیتابیس این دسته را می‌داند بی‌ضرر نیست.
 */
export type ErrorFamily =
  | 'none' | 'timeout' | 'model' | 'blocked' | 'http'
  | 'network' | 'db' | 'parse' | 'code' | 'unknown';

export const ERROR_FAMILY: Record<ErrorFamily, { label: string; why: string }> = {
  none: {
    label: 'بی‌خطا',
    why: 'هیچ متنِ خطایی برای این فاز ثبت نشده.',
  },
  timeout: {
    label: 'تایم‌اوت',
    why: 'کرالر درخواست را فرستاد و طرف در مهلتِ مقرر جواب نداد. مثل زنگ زدن '
      + 'به جایی که کسی گوشی را برنمی‌دارد — خطِ تلفن سالم است، آن‌طرف جواب نمی‌دهد.',
  },
  model: {
    label: 'مدلِ زبانی',
    why: 'خطا از سمتِ مدل آمده — سقفِ نرخ، سقفِ توکن، یا جوابی که به شکلِ '
      + 'مورد انتظار نبود. این خطاها معمولاً با تکرار خودبه‌خود رفع می‌شوند.',
  },
  blocked: {
    label: 'راهمان ندادند',
    why: 'سایت عمداً جلویمان را گرفته — ۴۰۳، ۴۲۹، یا `robots.txt`. با تکرار '
      + 'درست نمی‌شود؛ یا باید کندتر رفت یا آن منبع را کنار گذاشت.',
  },
  http: {
    label: 'جوابِ خطای سایت',
    why: 'سرور جواب داد ولی جوابش خطا بود (۴۰۴، ۵۰۰ و مانندش). مثل رفتن به '
      + 'آدرسی که تابلویش هست ولی مغازه بسته شده.',
  },
  network: {
    label: 'شبکه',
    why: 'اصلاً به سرور نرسیدیم — دامنه پیدا نشد، گواهیِ امنیتی خراب بود، یا '
      + 'اتصال قطع شد. مثل شماره‌ای که دیگر وجود ندارد.',
  },
  db: {
    label: 'دیتابیس',
    why: 'خطا از پستگرس آمده — کلیدِ تکراری، قیدِ نقض‌شده، یا جدولی که نیست. '
      + 'این یکی تقریباً همیشه یعنی کدِ کرالر و اسکیمای دیتابیس با هم نمی‌خوانند.',
  },
  parse: {
    label: 'خواندنِ محتوا',
    why: 'صفحه آمد ولی کرالر نتوانست بخواندش — ساختارِ HTML عوض شده، یا '
      + 'جوابی که JSON انتظارش می‌رفت JSON نبود. مثل نامه‌ای که رسیده ولی به '
      + 'خطی نوشته شده که بلد نیستیم.',
  },
  code: {
    label: 'ایرادِ خودِ کدِ کرالر',
    why: 'خطای زمانِ اجرای پایتون — نامی که تعریف نشده، نوعی که جور نیست، '
      + 'حافظه‌ای که ته کشیده. نه تقصیرِ سایت است نه تقصیرِ دیتابیس: یک باگ '
      + 'در خودِ کرالر که با تکرار هم درست نمی‌شود و باید در مخزنِ '
      + 'event_crawler تعمیر شود.',
  },
  unknown: {
    label: 'دسته‌بندی‌نشده',
    why: 'متنِ خطا با هیچ‌کدام از الگوهای شناخته‌شده نخواند. خودِ متن پایین '
      + 'آمده؛ اگر این دسته زیاد دیده شد یعنی باید الگوی تازه‌ای اضافه شود.',
  },
};

/**
 * ⚠ ترتیبِ این جدول **معنی‌دار است و جابه‌جا کردنش جواب را عوض می‌کند.**
 * یک متنِ خطا معمولاً به چند الگو می‌خورد («HTTP 429» هم `http` است هم
 * `blocked`) و اولین تطابق برنده است. پس مشخص‌ترها بالا می‌آیند:
 * «۴۲۹» بیشتر از «یک کدِ HTTP» حرف می‌زند، و «تایم‌اوت» از «شبکه».
 *
 * ⚠ نام‌های کلاسِ استثنای psycopg (`UndefinedTable`، `UniqueViolation`، …)
 * صریح آمده‌اند و حدس نیستند: همین‌ها الان در ستونِ `last_error`ِ دیتابیسِ
 * لوکال نشسته‌اند. بدونشان «relation crawler.sources does not exist» در
 * «دسته‌بندی‌نشده» می‌افتاد، در حالی که دقیقاً یک خطای دیتابیس است.
 *
 * و `code` دسته‌ی جداست چون جوابش فرق دارد: `NameError` و `MemoryError` نه
 * تقصیرِ سایت‌اند نه تقصیرِ دیتابیس — باگِ خودِ کرالرند و با تکرار درست
 * نمی‌شوند.
 */
const ERROR_PATTERNS: { family: ErrorFamily; re: RegExp }[] = [
  { family: 'timeout', re: /timeout|timed?[ _-]?out|deadline exceeded|ETIMEDOUT/i },
  { family: 'model', re: /anthropic|claude|openai|\bllm\b|rate[ _-]?limit|overloaded|max_tokens|429/i },
  { family: 'blocked', re: /robots|forbidden|\b40[13]\b|captcha|cloudflare|blocked|access denied/i },
  {
    family: 'db',
    re: /psycopg|postgres|sqlstate|duplicate key|violates|constraint|deadlock|relation "/i,
  },
  {
    family: 'db',
    re: /\w+Violation\b|Undefined(Table|Column|Function|Object|Parameter)|IndeterminateDatatype|InsufficientPrivilege|InFailedSqlTransaction/,
  },
  { family: 'http', re: /\bhttp[ _]?\d{3}\b|status[ _]?code|\b(404|410|5\d\d)\b|server error|bad gateway/i },
  { family: 'network', re: /dns|name or service|getaddrinfo|ssl|certificate|connection (reset|refused|aborted|error)|unreachable/i },
  { family: 'parse', re: /parse|decode|json|selector|no such element|unicode|invalid literal|malformed|not enough values/i },
  {
    family: 'code',
    re: /\b(NameError|TypeError|AttributeError|KeyError|IndexError|ValueError|ImportError|ModuleNotFoundError|MemoryError|ZeroDivisionError|RecursionError|NotImplementedError|UnboundLocalError)\b/,
  },
];

/** جنسِ خطا از روی متنش. `null`/خالی یعنی خطایی نبوده، نه اینکه ناشناخته باشد. */
export function classifyError(text: string | null | undefined): ErrorFamily {
  const s = (text ?? '').trim();
  if (!s) return 'none';
  for (const p of ERROR_PATTERNS) if (p.re.test(s)) return p.family;
  return 'unknown';
}

// ═══════════════════════════════════════════════════════════════════════
//  پنل‌ها — چیزی که یک عدد نیست و زیرِ همان مرحله می‌نشیند (۰۱۴۴)
// ═══════════════════════════════════════════════════════════════════════

/**
 * «چند درخواست، روی چند آدرسِ **یکتا**» — و این دو عدد کنارِ هم جوابِ
 * «فردا هم باز سراغش می‌رویم؟» است.
 *
 * ⚠ جوابش **بله** است. کشِ خطا جلوی تماسِ شبکه را می‌گیرد، نه جلوی وارد
 * شدنِ آدرس به صف: هر آدرسِ مرده هر شب دوباره سرِ صف می‌آید و دوباره رد
 * می‌شود. پس عددِ «۱۲٬۷۱۴ کشِ لینکِ مرده» می‌ترساند، در حالی که فقط ۱٬۶۸۲
 * آدرسِ یکتاست که هر کدام هشت بار شمرده شده. بدونِ این ستون، آدم دنبالِ
 * دوازده هزار لینکِ خراب می‌گردد که وجود ندارند.
 */
export interface RepeatRow {
  bucket: string;
  requests: number;
  urls: number;
  sources: number;
  /** درخواست به‌ازای هر آدرسِ یکتا، یک رقمِ اعشار. `null` یعنی آدرسی نبوده. */
  perUrl: number | null;
}

export interface ReasonRow {
  key: string;
  label: string;
  why: string;
  n: number;
  /** سهم از جمعِ همین پنل، درصد. */
  share: number | null;
}

export interface NotSentRow {
  title: string;
  url: string | null;
  source: string | null;
  why: string;
  whyLabel: string;
  at: string;
}

export type Panel =
  | { kind: 'repeat'; title: string; note: string; rows: RepeatRow[] }
  | { kind: 'reasons'; title: string; note: string; rows: ReasonRow[] }
  | { kind: 'notsent'; title: string; note: string; rows: NotSentRow[] };

/**
 * دلیل‌هایی که خودِ کرالر می‌نویسد، به فارسی.
 *
 * ⚠ رشته‌ها اختراع نشده‌اند: عیناً همان چیزی‌اند که در
 * `crawler.publish_decisions.reasons` و `crawler.source_drafts.promote_outcome`
 * نشسته‌اند. دلیلی که این‌جا نباشد **خام** نشان داده می‌شود و نه «نامعلوم»:
 * رشته‌ی خام دستِ‌کم قابلِ جست‌وجوست، «نامعلوم» هیچ‌چیز نیست.
 */
export const REASON_TEXT: Record<string, { label: string; why: string }> = {
  'missing_required:starts_at': {
    label: 'تاریخ ندارد',
    why: 'هیچ‌کدام از منبع‌ها تاریخِ شروع را نداده‌اند. بدونِ تاریخ نه می‌شود '
      + 'در تقویم نشانش داد نه می‌شود گفت گذشته یا آینده — پس منتشر نمی‌شود.',
  },
  'missing_required:title': {
    label: 'عنوان ندارد',
    why: 'متنی که بشود اسمِ رویداد صدایش کرد پیدا نشد. معمولاً یعنی تکه‌ی '
      + 'اشتباهی از صفحه به‌عنوان رویداد برداشته شده.',
  },
  field_conflict: {
    label: 'دو منبع دو چیز می‌گویند',
    why: 'دو سایت درباره‌ی همین رویداد دو مقدارِ متفاوت داده‌اند (مثلاً دو '
      + 'ساعتِ شروع یا دو آدرس). کرالر خودش انتخاب نمی‌کند و می‌فرستدش به صفِ '
      + 'بازبینی تا یک آدم بگوید کدام درست است.',
  },
  no_authoritative_source: {
    label: 'منبعِ معتبر ندارد',
    why: 'فقط سایت‌هایی گفته‌اندش که در رجیستری «معتبر» شمرده نمی‌شوند — مثلاً '
      + 'یک تقویمِ جمع‌آوری‌شده به‌جای سایتِ خودِ برگزارکننده. ممکن است درست '
      + 'باشد، ولی کسی ضمانتش نمی‌کند.',
  },
  unstable_facebook_image: {
    label: 'عکسِ ناپایدارِ فیسبوک',
    why: 'تنها عکسِ رویداد لینکِ موقتیِ فیسبوک است که چند روز بعد از کار '
      + 'می‌افتد. اگر منتشر شود، کاربر بعداً یک کارتِ بی‌عکس می‌بیند.',
  },
  promoted: {
    label: 'ترفیع گرفت',
    why: 'به رجیستری اضافه شد و از حالا در فهرستِ منابع است.',
  },
};

/** برچسبِ دو حالتِ «نرفته» — تفکیکی که ۰۱۴۴ آورد. */
export const NOTSENT_WHY: Record<string, { label: string; why: string }> = {
  in_app: {
    label: 'در اپ هست، فقط ثبت نشده',
    why: 'کاربر این رویداد را در اپ می‌بیند — ردیفش در `public.events` هست. '
      + 'چیزی که نیست، ردیفِ **دفترِ خودِ کرالر** است. یعنی این یکی گم نشده و '
      + 'دنبالش گشتن وقت تلف کردن است؛ ایراد در بایگانیِ کرالر است نه در اپ.',
  },
  gone: {
    label: 'واقعاً نرفته',
    why: 'نه ردیفی در اپ دارد نه شناسه‌ای گرفته. این یکی را کاربر نمی‌بیند و '
      + 'اگر تعدادش بالا رفت یعنی آخرین مرحله نچرخیده.',
  },
};

const reasonText = (key: string): { label: string; why: string } =>
  REASON_TEXT[key] ?? {
    label: key,
    // رشته‌ی خام و نه «نامعلوم»: دستِ‌کم قابلِ جست‌وجو در مخزنِ کرالر است.
    why: 'این دلیل را کرالر نوشته و این صفحه هنوز ترجمه‌ای برایش ندارد. '
      + 'خودِ رشته عیناً همان چیزی است که در دیتابیس نشسته.',
  };

/** کارنامه‌ی **خودِ مرحله** — جدا از سرنوشتِ آیتم‌هایی که از تویش رد شده‌اند. */
export interface StageHealth {
  runs: Pair;
  failures: Pair;
  /** درصدِ شکست در ده روز، یک رقمِ اعشار. `null` یعنی اصلاً نچرخیده. */
  failRate: number | null;
  /** ثانیه؛ `null` یعنی «نچرخید»، نه «صفر ثانیه». */
  p50: number | null;
  p95: number | null;
  /**
   * صدکِ ۹۵ بیش از پنج برابرِ میانه — یعنی بیشترِ اجراها سریع‌اند ولی یک
   * دنباله‌ی بلند هست. همان قاعده‌ای که جدولِ فازها هم رویش قرمز می‌کند.
   */
  slow: boolean;
  lastRun: string | null;
  lastOk: string | null;
  lastError: string | null;
  errorFamily: ErrorFamily;
}

export interface StageRow {
  id: string;
  /** نامِ فاز در `crawler.runs` — همان که در ARCHITECTURE-VNEXT.md است. */
  stage: string;
  title: string;
  /** واحدِ کارِ این مرحله: «یک آدرس»، «یک سند»، «یک کاندیدا»… */
  unit: string;
  /** این مرحله دقیقاً چه کار می‌کند، بدونِ اصطلاح. */
  what: string;
  /** همان، با یک مثالِ دنیای واقعی. */
  like: string;
  input: { label: string; v: Pair } | null;
  output: { label: string; v: Pair } | null;
  /** ⚠ ورودی و خروجی هم‌واحد نیستند و این باید گفته شود، نه پنهان. */
  unitShift: string | null;
  fates: Fate[];
  /**
   * ورودی منهای جمعِ سرنوشت‌های غیرِ `aside`. صفر یعنی حساب تمیز است.
   *
   * ⚠ مرحله‌ای که اصلاً سرنوشت ندارد (کشف) همیشه صفر می‌گیرد: آن‌جا ورودی
   * تقسیم نمی‌شود چون ورودی و خروجی دو چیزِ متفاوت‌اند («یک بار پرسیدن» در
   * برابرِ «یک سایت»). گذاشتنِ کلِ ورودی در «حساب‌نشده» یعنی قرمز کردنِ
   * چیزی که خراب نیست.
   */
  residual: number;
  /**
   * جدول‌هایی که یک عدد نیستند و زیرِ همان مرحله می‌نشینند (۰۱۴۴).
   *
   * جدا از `fates` است چون جوابِ سوالِ دیگری‌اند: `fates` می‌گوید **چقدر**،
   * پنل می‌گوید **چرا** و **کدام‌ها**.
   */
  panels: Panel[];
  health: StageHealth | null;
  /** این فاز هیچ ردیفی در `crawler.runs` ندارد — یعنی در این پنجره نچرخیده. */
  idle: boolean;
}

export interface StageReport {
  rows: StageRow[];
  /**
   * فازهایی که `crawler.runs` دارد و این فایل نمی‌شناسدشان.
   *
   * ⚠ دور ریخته نمی‌شوند. اگر کرالر فازِ تازه‌ای اضافه کند، چیزی که آدم
   * باید ببیند دقیقاً همین است — نه صفحه‌ای که انگار آن فاز وجود ندارد.
   */
  unknown: StageRun[];
}

/** منفی و NaN به صفر — یک عددِ منفی کلِ نوارِ سرنوشت را به‌هم می‌ریزد. */
const n0 = (n: number | undefined | null): number =>
  (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

const pair = (d10: number, run: number): Pair => ({ d10, run });

/**
 * تعریفِ یک مرحله، مستقل از عدد.
 *
 * `read` دو بار صدا زده می‌شود — یک بار برای ستونِ ده‌روزه و یک بار برای
 * آخرین اجرا — و هر بار فقط عدد می‌دهد. جدا نگه داشتنِ متن از عدد یعنی
 * متن‌ها یک نسخه بیشتر ندارند و دو ستون نمی‌توانند دو چیزِ متفاوت بگویند.
 */
interface StageDef {
  id: string;
  /**
   * نام‌هایی که در `crawler.runs` ممکن است این فاز را صدا بزنند. اولی اصلی
   * است و اولین تطابق برنده.
   *
   * ⚠ **این نام‌ها حدس نیستند و از خودِ دیتابیس خوانده شده‌اند.** نسخه‌ی اولِ
   * این فایل اسمِ کوتاه (`detect`، `match`، `policy`) را گذاشته بود چون
   * زنجیره در `ARCHITECTURE-VNEXT.md` این‌طور نوشته شده؛ ولی چیزی که کرالر
   * واقعاً در `crawler.runs` می‌نویسد `vnext_detect`، `vnext_match` و
   * `vnext_publish_policy` است. نتیجه‌اش این بود که هشت مرحله از ده مرحله
   * «ردیفِ اجرا ندارد» نشان می‌دادند و همه‌ی ردیف‌های واقعی در فهرستِ
   * «فازهای ناشناخته» می‌ریختند. اسمِ کوتاه هم برای احتیاط می‌ماند، بی‌ضرر
   * است و اگر روزی کرالر پیشوند را برداشت خودش می‌خورد.
   *
   * ⚠ `extract` عمداً به `claim` نگاشت **نشده**: آن فازِ خطِ لوله‌ی قدیم
   * است و `vnext_claims` فازِ vNext. قاتی کردنِ دو نسل، عددِ یکی را به پای
   * دیگری می‌نویسد.
   */
  stages: string[];
  title: string;
  unit: string;
  what: string;
  like: string;
  inputLabel: string;
  outputLabel: string;
  unitShift?: string;
  /** ورودی، خروجی و تقسیمِ ورودی — همه از یک ستونِ شمارش. */
  read: (c: StageCounts) => {
    input: number;
    output: number;
    fates: { id: string; value: number }[];
  };
  /** متنِ هر سرنوشت. کلیدش همان `id`ِ بالاست. */
  fates: Record<string, { label: string; kind: FateKind; why: string; like?: string; aside?: boolean }>;
}

/**
 * ده مرحله، به ترتیبی که خطِ لوله واقعاً طی می‌کند.
 *
 * ⚠ مثال‌های «مثلِ…» یک استعاره‌ی واحد را دنبال می‌کنند و این عمدی است:
 * کسی را فرستاده‌ای در شهر بگردد و اعلانِ رویدادها را جمع کند. استعاره‌ی
 * پراکنده بدتر از بی‌استعاره است — آدم باید هر بار از نو خودش را جا بیندازد.
 */
const DEFS: StageDef[] = [
  // ═══ سه مرحله‌ی **قبل از** گرفتنِ صفحه (۰۱۴۴) ═══
  //
  // ⚠ تا ۰۱۴۴ نمودار از «گرفتنِ صفحه» شروع می‌شد، انگار فهرستِ منابع از
  // آسمان آمده. نیامده، و نبودنشان در نمودار یعنی بزرگ‌ترین سوالِ خطِ لوله
  // («چرا این منبع اصلاً نیست؟») هیچ‌جا جوابی نداشت.
  {
    id: 'discover',
    stages: ['discover', 'vnext_discovery', 'discovery'],
    title: 'کشفِ منبع',
    unit: 'یک نامزدِ سایت',
    what: 'برای یک شهر از مدلِ زبانی پرسیده می‌شود «چه سایت‌هایی این‌جا '
      + 'رویداد اعلام می‌کنند؟» و جوابش به‌شکلِ فهرستی از سایت‌ها برمی‌گردد.',
    like: 'مثلِ پرسیدن از یک محلیِ آن شهر که «تابلوهای اعلاناتِ این شهر '
      + 'کجاست؟». هنوز هیچ تابلویی دیده نشده — فقط نشانی گرفته‌ایم.',
    inputLabel: 'پرسشِ کشف',
    outputLabel: 'سایتِ پیشنهادشده',
    unitShift: 'ورودی «یک بار پرسیدن» است و خروجی «یک سایت»؛ هر پرسش چند '
      + 'سایت برمی‌گرداند، پس این دو هم‌واحد نیستند. ⚠ کشف گران است (هر بار '
      + 'پولِ مدل) و برای همین شبی **یک شهر** بیشتر اجرا نمی‌شود.',
    read: (c) => ({
      input: n0(c.disc_runs),
      output: n0(c.disc_candidates),
      fates: [],
    }),
    // ⚠ `read` هیچ سرنوشتی برنمی‌گرداند (ورودی و خروجیِ این مرحله هم‌واحد
    // نیستند و تقسیمشان معنی ندارد)، ولی متنِ این دسته این‌جا می‌ماند چون
    // جدولِ نمونه‌ها با همین شناسه برچسب می‌گیرد.
    fates: {
      candidate: {
        label: 'سایتِ پیشنهادشده', kind: 'pass',
        why: 'سایتی که مدل گفت شاید در این شهر رویداد اعلام کند. هنوز هیچ‌کس '
          + 'بازش نکرده — فقط یک نشانی است.',
      },
    },
  },
  {
    id: 'inspect',
    stages: ['inspect', 'vnext_inspect', 'inspection'],
    title: 'بررسیِ سایت',
    unit: 'یک بررسی',
    what: 'صفحه‌ی هر سایتِ پیشنهادشده یک بار واقعاً گرفته می‌شود تا معلوم شود '
      + 'اصلاً باز می‌شود، رویدادی دارد، و ساختارش با استخراج‌گرهای ما خوانده '
      + 'می‌شود یا نه.',
    like: 'مثلِ رفتن سرِ هر نشانی‌ای که گرفته‌ای و نگاه کردن: تابلو هست؟ '
      + 'کاغذی رویش هست؟ خطش خوانا هست؟',
    inputLabel: 'بررسی',
    outputLabel: 'به‌دردبخور',
    read: (c) => {
      const total = n0(c.insp_total);
      const usable = Math.min(n0(c.insp_usable), total);
      const blocked = Math.min(n0(c.insp_blocked), total - usable);
      return {
        input: total,
        output: usable,
        fates: [
          { id: 'usable', value: usable },
          { id: 'unusable', value: Math.min(n0(c.insp_unusable), total - usable - blocked) },
          { id: 'blocked', value: blocked },
        ],
      };
    },
    fates: {
      usable: {
        label: 'به‌دردبخور', kind: 'pass',
        why: 'باز شد، رویداد داشت، و ساختارش خوانده می‌شود. نامزدِ ترفیع به رجیستری.',
      },
      unusable: {
        label: 'باز شد ولی به درد نخورد', kind: 'drop',
        why: 'صفحه سالم آمد ولی یا رویدادی نداشت، یا خالی بود، یا ساختارش با '
          + 'هیچ‌کدام از استخراج‌گرهای امروز خوانده نمی‌شود.',
        like: 'مثلِ تابلویی که هست ولی کاغذهایش را برداشته‌اند، یا به خطی '
          + 'نوشته شده که بلد نیستیم.',
      },
      blocked: {
        label: 'اصلاً نشد نگاهش کرد', kind: 'drop',
        why: 'صفحه نیامد — ۴۰۳، ۴۰۴، `robots.txt`، یا تایم‌اوت. ⚠ با «به درد '
          + 'نخورد» یکی نیست و یکی شمردنشان گمراه‌کننده است: شاید سایتِ خوبی '
          + 'باشد که فقط ما را راه نداده.',
      },
    },
  },
  {
    id: 'promote',
    stages: ['promote', 'vnext_promote', 'sync_registry'],
    title: 'ترفیع به رجیستری',
    unit: 'یک پیش‌نویسِ منبع',
    what: 'پیش‌نویسی که از بررسی سربلند بیرون آمد به فهرستِ رسمیِ منابع '
      + 'اضافه می‌شود. از آن لحظه هر شب صفحه‌هایش گرفته می‌شود.',
    like: 'مثلِ نوشتنِ نشانیِ آن تابلو در دفترچه‌ی مسیرِ روزانه. از فردا هر '
      + 'روز از کنارش رد می‌شوی.',
    inputLabel: 'پیش‌نویس',
    outputLabel: 'منبعِ رسمی',
    read: (c) => {
      const total = n0(c.drafts_total);
      const promoted = Math.min(n0(c.drafts_promoted), total);
      const rejected = Math.min(n0(c.drafts_rejected), total - promoted);
      return {
        input: total,
        output: promoted,
        fates: [
          { id: 'promoted', value: promoted },
          { id: 'rejected', value: rejected },
          { id: 'waiting', value: Math.min(n0(c.drafts_pending), total - promoted - rejected) },
          { id: 'reg_on', value: n0(c.sources_enabled) },
          { id: 'reg_off', value: n0(c.sources_disabled) },
        ],
      };
    },
    fates: {
      promoted: {
        label: 'ترفیع گرفت', kind: 'pass',
        why: 'به رجیستری اضافه شد و از حالا هر شب سراغش می‌رویم.',
      },
      rejected: {
        label: 'رد شد', kind: 'drop',
        why: 'به رجیستری راه نیافت. دلیل‌هایش در جدولِ پایینِ همین مرحله '
          + 'آمده — خودِ رشته‌ای که کرالر نوشته.',
      },
      waiting: {
        label: 'منتظرِ حکم', kind: 'hold',
        why: 'هنوز نه ترفیع گرفته نه رد شده. ⚠ زنجیره‌ی شبانه شبی حداکثر '
          + '۲۰ پیش‌نویس را بررسی می‌کند، پس صف طبیعی است — ولی صفی که '
          + 'هفته‌ها بماند یعنی سرعتِ کشف از سرعتِ بررسی جلو زده.',
      },
      reg_on: {
        label: 'الان روشن در رجیستری', kind: 'info', aside: true,
        why: 'شمارِ منابعی که **همین حالا** فعال‌اند. عددِ حالت است، نه '
          + 'جریان: هر سه ستونش یکی است و این عمدی است.',
      },
      reg_off: {
        label: 'الان خاموش در رجیستری', kind: 'info', aside: true,
        why: 'منبعی که در رجیستری هست ولی خاموش شده — معمولاً چون مدتی است '
          + 'چیزی نمی‌دهد یا مشکلی داشته. ⚠ آدرس‌هایش **هنوز در صفِ گرفتنِ '
          + 'صفحه می‌آیند** و سرِ صف رد می‌شوند؛ جدولِ تکرارِ مرحله‌ی بعد '
          + 'نشان می‌دهد این چقدر عددها را باد می‌کند.',
      },
    },
  },
  {
    id: 'fetch',
    stages: ['fetch'],
    title: 'گرفتنِ صفحه',
    unit: 'یک آدرس',
    what: 'هر آدرسی که در صف بود، گرفته می‌شود و بدنه‌اش نگه داشته می‌شود.',
    like: 'مثلِ رفتن سرِ تابلوی اعلاناتِ چند جای شهر و عکس گرفتن از کلِ تابلو. '
      + 'هنوز هیچ‌کس هیچ اعلانی را نخوانده — فقط عکس‌ها گرفته شده‌اند.',
    inputLabel: 'آدرسِ در صف',
    outputLabel: 'صفحه‌ی تازه',
    read: (c) => {
      const ok = n0(c.fetch_ok);
      const unchanged = n0(c.fetch_unchanged);
      const error = n0(c.fetch_errors);
      const skipped = n0(c.fetch_skipped);
      const blocked = n0(c.fetch_blocked);
      // سه دلیلِ «مسدود» تضمینی نیستند — کرالر می‌تواند دلیلِ تازه بنویسد —
      // پس هر کدام از سهمِ باقی‌مانده بریده می‌شود و ته‌مانده‌شان در
      // `residual` می‌ماند و بی‌صدا گم نمی‌شود.
      const robots = Math.min(n0(c.fetch_blocked_robots), blocked);
      const tier = Math.min(n0(c.fetch_blocked_tier), blocked - robots);
      const disabled = Math.min(n0(c.fetch_blocked_disabled), blocked - robots - tier);
      return {
        // از جمعِ سطل‌ها و نه از `fetch_requests`: همان عددی که واقعاً تقسیم
        // می‌شود. اگر روزی مقدارِ ششمی به `outcome` اضافه شود، این دو از هم
        // فاصله می‌گیرند و مرحله باید همان را بگوید که تجزیه کرده.
        input: ok + unchanged + error + skipped + blocked,
        output: ok,
        fates: [
          { id: 'ok', value: ok },
          { id: 'unchanged', value: unchanged },
          { id: 'error', value: error },
          { id: 'skipped', value: skipped },
          { id: 'robots', value: robots },
          { id: 'tier', value: tier },
          { id: 'disabled', value: disabled },
          { id: 'blocked_other', value: Math.max(blocked - robots - tier - disabled, 0) },
        ],
      };
    },
    fates: {
      ok: {
        label: 'گرفته شد', kind: 'pass',
        why: 'صفحه آمد و بدنه‌اش سندِ خام شد. کارِ مرحله‌ی بعد از همین‌جا شروع می‌شود.',
      },
      unchanged: {
        label: 'عوض نشده بود', kind: 'info',
        why: 'رفتیم و سرور گفت از دفعه‌ی قبل چیزی عوض نشده (جوابِ ۳۰۴). '
          + '**این کشِ واقعی است** و خبرِ خوبی هم هست: هزینه‌ی شبکه داد ولی کارِ بیهوده نکرد.',
        like: 'مثلِ رفتن سرِ تابلو و دیدنِ اینکه همان اعلان‌های دیروز چسبیده‌اند.',
      },
      error: {
        label: 'خطا', kind: 'drop',
        why: 'صفحه اصلاً گرفته نشد — خطای شبکه، تایم‌اوت، یا کدِ ۴۰۰ به بالا. '
          + 'سه بارِ پیاپیِ این، آدرس را به «کشِ لینکِ مرده» می‌فرستد.',
      },
      skipped: {
        label: 'کشِ لینکِ مرده', kind: 'drop',
        why: 'آدرسی که سه بار پشتِ سرِ هم خطا داده؛ کرالر تا هفت روز اصلاً '
          + 'سراغش نمی‌رود. با «عوض نشده بود» یکی نیست — این یکی هرگز به شبکه نرفت.',
        like: 'مثلِ تابلویی که سه بار رفتی و هر بار در بسته بود؛ یک هفته دیگر سراغش نمی‌روی.',
      },
      robots: {
        label: 'robots.txt', kind: 'drop',
        why: 'خودِ سایت در فایلِ `robots.txt` این مسیر را برای ربات‌ها ممنوع کرده. '
          + 'کارِ ما نیست — یا منبع باید کنار گذاشته شود یا مسیرِ دیگری از همان سایت.',
        like: 'مثلِ تابلویی که رویش نوشته «عکس گرفتن ممنوع».',
      },
      tier: {
        label: 'حکمِ دسترسی', kind: 'drop',
        why: 'سیاستِ دسترسیِ **خودمان** اجازه نداد (ستونِ `tier`ِ آن منبع در رجیستری). '
          + 'برخلافِ `robots.txt`، این یکی با یک ویرایش در سربرگِ «منابع» عوض می‌شود.',
      },
      disabled: {
        label: 'منبعِ خاموش', kind: 'drop',
        why: 'منبع در رجیستری خاموش است، ولی آدرس‌هایش هنوز در صف می‌آیند و سرِ '
          + 'صف رد می‌شوند. بی‌ضرر است، ولی بی‌فایده هم هست.',
      },
      blocked_other: {
        label: 'مسدودِ دیگر', kind: 'drop',
        why: 'مسدود، با دلیلی که این گزارش هنوز اسمی برایش ندارد. اگر این عدد '
          + 'دیده شد یعنی کرالر دلیلِ تازه‌ای می‌نویسد و باید به فهرست اضافه شود.',
      },
    },
  },
  {
    id: 'detect',
    stages: ['vnext_detect', 'detect'],
    title: 'پیدا کردنِ آیتم‌ها',
    unit: 'یک سند',
    what: 'صفحه‌ی گرفته‌شده تکه‌تکه می‌شود تا معلوم شود چند «چیزِ رویدادمانند» تویش هست.',
    like: 'مثلِ نگاه کردن به عکسِ تابلو و شمردنِ کاغذهایی که رویش چسبیده‌اند. '
      + 'هنوز هیچ‌کدام خوانده نشده — فقط شمرده شده‌اند.',
    inputLabel: 'سندِ خام',
    outputLabel: 'کاندیدا',
    unitShift: 'ورودی «سند» است و خروجی «کاندیدا». یک صفحه‌ی فهرست می‌تواند '
      + 'سی کاندیدا بدهد، پس بزرگ‌تر بودنِ خروجی از ورودی درست است و خرابی نیست.',
    read: (c) => {
      const docs = n0(c.detect_documents) || n0(c.docs_listing) + n0(c.docs_detail);
      const barren = Math.min(n0(c.detect_barren), docs);
      return {
        input: docs,
        output: n0(c.candidates),
        fates: [
          { id: 'fruitful', value: Math.max(docs - barren, 0) },
          { id: 'barren', value: barren },
        ],
      };
    },
    fates: {
      fruitful: {
        label: 'دستِ‌کم یک آیتم داشت', kind: 'pass',
        why: 'صفحه تکه شد و دستِ‌کم یک کاندیدا داد.',
      },
      barren: {
        label: 'صفحه‌ی بی‌آیتم', kind: 'drop',
        why: 'صفحه سالم گرفته شد و تکه شد ولی **صفر** کاندیدا داد — یعنی الگوی '
          + 'تشخیص با ساختارِ آن سایت نمی‌خواند. خرابیِ بی‌صدا: نه خطایی ثبت '
          + 'می‌شود نه چیزی قرمز می‌شود.',
        like: 'مثلِ عکس گرفتن از تابلویی که کاغذهایش را برداشته‌اند — عکس سالم است، تابلو خالی.',
      },
    },
  },
  {
    id: 'expand',
    stages: ['vnext_expand', 'expand'],
    title: 'باز کردنِ جزئیات',
    unit: 'یک کاندیدا',
    what: 'برای کاندیدایی که در فهرست لینکِ «جزئیات» داشت، آن صفحه هم گرفته '
      + 'می‌شود تا فیلدهای بیشتری در بیاید.',
    like: 'مثلِ کاغذی که رویش نوشته «جزئیات در سایتِ ما» — می‌روی و آن صفحه را هم می‌خوانی.',
    inputLabel: 'کاندیدا',
    outputLabel: 'کاندیدای جزئیات‌دار',
    read: (c) => {
      const cand = n0(c.candidates);
      const attached = Math.min(n0(c.cand_expand_attached), cand);
      return {
        input: cand,
        output: attached,
        fates: [
          { id: 'attached', value: attached },
          { id: 'listing_only', value: Math.max(cand - attached, 0) },
        ],
      };
    },
    fates: {
      attached: {
        label: 'صفحه‌ی خودش هم گرفته شد', kind: 'pass',
        why: 'علاوه بر ردیفِ فهرست، صفحه‌ی اختصاصیِ خودِ رویداد هم گرفته شد. '
          + 'این‌ها بیشترین شانس را دارند که تاریخ و آدرسِ درست بدهند.',
      },
      listing_only: {
        label: 'فقط از روی فهرست', kind: 'info',
        why: 'یا لینکِ جزئیاتی نداشت یا لازم نبود. **ریزش نیست** — خیلی از '
          + 'فهرست‌ها خودشان عنوان و تاریخ را دارند.',
      },
    },
  },
  {
    id: 'claim',
    stages: ['vnext_claims', 'claim'],
    title: 'استخراجِ فیلدها',
    unit: 'یک کاندیدا',
    what: 'از متنِ هر کاندیدا فیلدهایش بیرون کشیده می‌شود: عنوان، تاریخ، ساعت، '
      + 'مکان، قیمت. هر فیلدِ استخراج‌شده یک «ادعا» است.',
    like: 'مثلِ خواندنِ کاغذ و یادداشت کردنِ اینکه «اسم: کنسرتِ فلان، تاریخ: '
      + 'جمعه، جا: سالنِ شهر». هنوز کسی نگفته این یادداشت کامل است یا نه.',
    inputLabel: 'کاندیدایی که استخراج رویش دوید',
    outputLabel: 'ادعا',
    unitShift: 'ورودی «کاندیدا» است و خروجی «ادعا»؛ از یک کاندیدا چند ادعا '
      + 'در می‌آید (عنوان، تاریخ، مکان…)، پس این دو عدد هم‌واحد نیستند.',
    read: (c) => {
      const ran = n0(c.cand_claims_run) || n0(c.candidates);
      const none = Math.min(n0(c.cand_claims_none), ran);
      return {
        input: ran,
        output: n0(c.claims),
        fates: [
          { id: 'some', value: Math.max(ran - none, 0) },
          { id: 'none', value: none },
        ],
      };
    },
    fates: {
      some: {
        label: 'دستِ‌کم یک فیلد درآمد', kind: 'pass',
        why: 'استخراج چیزی از این کاندیدا بیرون کشید. لزوماً کافی نیست — کافی '
          + 'بودن را مرحله‌ی بعد تصمیم می‌گیرد.',
      },
      none: {
        label: 'کاندیدای بی‌ادعا', kind: 'drop',
        why: 'تکه‌ای که کاندیدا شمرده شد ولی استخراج **هیچ** فیلدی از آن درنیاورد. '
          + 'معمولاً یعنی آن تکه اصلاً رویداد نبوده (منو، تبلیغ، پاورقی).',
        like: 'مثلِ کاغذی که از دور شبیهِ اعلان بود و از نزدیک معلوم شد آگهیِ فروشِ خانه است.',
      },
    },
  },
  {
    id: 'mention',
    stages: ['vnext_mentions', 'mention'],
    title: 'ساختنِ mention',
    unit: 'یک کاندیدا',
    what: 'تصمیمِ «آیا آن‌قدر فیلد داریم که بشود گفت این یک رویدادِ مشخص است؟». '
      + 'کفِ لازم عنوان و تاریخ است. هر کاندیدایی که از این کف رد شود یک '
      + '«mention» می‌شود — یعنی «یک بار دیدنِ یک رویداد، از یک منبع».',
    like: 'مثلِ جدا کردنِ یادداشت‌هایی که هم اسمِ برنامه را دارند هم روزش. '
      + 'یادداشتی که فقط نوشته «کنسرت» بی‌فایده است.',
    inputLabel: 'کاندیدا',
    outputLabel: 'mention',
    // ⚠ **این توضیح جوابِ یک سوالِ واقعی است: «مگر کارش فیلتر کردن نیست؟
    // چطور خروجی از ورودی بیشتر شده؟»**
    //
    // چون mention واحدِ کاندیدا نیست. یک ردیفِ فهرست که سه سانس دارد یک
    // کاندیداست و سه mention می‌دهد. روی داده‌ی واقعی ۲۳۳٬۹۳۳ کاندیدا
    // ۲۵۵٬۵۶۸ mention داده‌اند. پس دو عددِ این مرحله **همزمان** درست‌اند:
    // بیشترِ کاندیداها می‌ریزند، و آن‌هایی که می‌مانند بیش از یکی می‌دهند.
    unitShift: 'ورودی «کاندیدا» است و خروجی «mention»، و این‌ها هم‌واحد '
      + 'نیستند. یک کاندیدا می‌تواند **چند** mention بدهد: یک ردیفِ فهرست که '
      + 'سه سانس دارد، سه بار دیدنِ رویداد است. برای همین ممکن است خروجی از '
      + 'ورودی بیشتر شود، بی‌آنکه فیلتر کار نکرده باشد — نوارِ رنگیِ زیر، که '
      + 'ورودی را می‌شمارد، می‌گوید واقعاً چقدر ریخت.',
    read: (c) => {
      const cand = n0(c.candidates);
      // ⚠ `cand_insufficient` و نه `cand_no_mention`، هر جا که باشد: آن یکی
      // با زمانِ جدولِ برآیند پنجره می‌خورد و این یکی با زمانِ خودِ کاندیدا.
      // روی داده‌ی واقعی همین تفاوت جمعِ چهار سطل را ۳٬۹۷۵ تا از ورودی
      // بزرگ‌تر می‌کرد. `cand_no_mention` فقط جانشینِ وقتی است که کش هنوز
      // کلیدهای ۰۱۴۴ را ندارد.
      const none = Math.min(
        n0(c.cand_insufficient ?? c.cand_no_mention), cand,
      );
      // ⚠ **`cand_built` و نه `mentions`.** تا مایگریشنِ ۰۱۴۴ این خانه
      // `mentions` را می‌گذاشت، یعنی شمارِ **mention** را در ستونی که واحدش
      // **کاندیدا** است. روی شهرِ bielefeld همین یک اشتباه جمع را از ورودی
      // بزرگ‌تر می‌کرد (۱۰۷۱ + ۳۲۹ روی ۱۳۰۰ کاندیدا) و نوار از کادر می‌زد
      // بیرون — دقیقاً همان دروغِ بی‌صدایی که سرِ این فایل توضیح داده شده.
      const built = c.cand_built === undefined
        ? Math.min(n0(c.mentions), Math.max(cand - none, 0))
        : Math.min(n0(c.cand_built), cand);
      const rest = Math.max(cand - built - none, 0);
      // ⚠ دو دلیلِ «در صف»، و تفاوتشان مهم است: یکی هنوز نوبتش نرسیده و
      // دیگری نوبتش رسیده ولی کار نیمه‌کاره مانده. تا ۰۱۴۴ هر دو یک عددِ
      // بی‌دلیل بودند که از تفریق در می‌آمد.
      const noBuild = Math.min(n0(c.cand_pending_no_build), rest);
      const noExtract = c.cand_pending_no_extract === undefined
        ? 0
        : Math.min(n0(c.cand_pending_no_extract), rest - noBuild);
      return {
        input: cand,
        output: n0(c.mentions),
        fates: [
          { id: 'mention', value: built },
          { id: 'nomention', value: none },
          { id: 'pending_no_build', value: noBuild },
          { id: 'pending_no_extract', value: noExtract },
          // ته‌مانده‌ی بی‌دلیل. تا وقتی کش کلیدهای ۰۱۴۴ را ندارد، **همه‌ی**
          // صف این‌جا می‌نشیند و متنش همین را می‌گوید.
          { id: 'pending', value: Math.max(rest - noBuild - noExtract, 0) },
        ],
      };
    },
    fates: {
      mention: {
        label: 'شد mention', kind: 'pass',
        why: 'عنوان و تاریخ داشت، پس حالا می‌شود گفت یک رویداد است. هنوز یکتا '
          + 'نیست: همین رویداد از منبعِ دیگر mentionِ جداگانه‌ای دارد. ⚠ این '
          + 'عدد **کاندیدا** می‌شمارد؛ خروجیِ بالای مرحله mention می‌شمارد و '
          + 'از این بزرگ‌تر است، چون یک کاندیدا می‌تواند چند سانس بدهد.',
      },
      nomention: {
        label: 'فیلدِ ناکافی', kind: 'drop',
        why: 'ادعا داشت ولی نه آن‌قدر که بشود گفت یک رویداد است (عنوان + تاریخ). '
          + 'حکمِ خودِ کرالر است: `non-event-insufficient-claims`.',
        like: 'مثلِ یادداشتی که نوشته «موسیقیِ زنده، آخرِ هفته» — نه اسم دارد نه تاریخ.',
      },
      pending_no_extract: {
        label: 'هنوز استخراج نشده', kind: 'hold',
        why: 'هیچ استخراجی روی این کاندیدا نرفته — نه اینکه شکست خورده باشد، '
          + 'اصلاً نوبتش نرسیده. **بزرگ‌ترین سطلِ این مرحله و بزرگ‌ترین صفِ کلِ '
          + 'خطِ لوله**: روی داده‌ی واقعی حدودِ دو سومِ کاندیداهای ده روز.',
        like: 'مثلِ کارتنی پر از کاغذ که جمع کرده‌ای و هنوز بازش نکرده‌ای.',
      },
      pending_no_build: {
        label: 'استخراج شد، mention نشد', kind: 'hold',
        why: 'فیلدهایش در آمد ولی مرحله‌ی ساختِ mention هنوز رویش نرفته. '
          + 'کارِ نیمه‌کاره است، نه ریزش — و اگر این عدد بزرگ بود یعنی '
          + 'مرحله‌ی mention از مرحله‌ی استخراج عقب مانده.',
      },
      pending: {
        label: 'در صفِ کار (بی‌دلیل)', kind: 'hold',
        why: 'نه mention شد، نه حکمِ «فیلدِ ناکافی» گرفت، و دلیلِ ماندنش هم در '
          + 'گزارش نیست. ⚠ اگر این عدد بزرگ است یعنی کشِ گزارش هنوز کلیدهای '
          + 'مایگریشنِ ۰۱۴۴ را ندارد و تفکیکِ دلیل‌ها یک اجرای کرالر دیرتر '
          + 'می‌آید؛ اگر کوچک است، یک حالتِ تازه در دیتابیس هست.',
      },
    },
  },
  {
    id: 'match',
    stages: ['vnext_match', 'match'],
    title: 'جفت کردن',
    unit: 'یک جفتِ mention',
    what: 'هر دو mention با هم مقایسه می‌شوند تا معلوم شود همان یک رویدادند یا نه.',
    like: 'مثلِ گذاشتنِ دو یادداشت کنارِ هم و پرسیدنِ «این همان کنسرت است؟». '
      + 'گاهی جواب روشن است و گاهی نه.',
    inputLabel: 'جفتِ سنجیده‌شده',
    outputLabel: 'جفتِ «همان است»',
    read: (c) => {
      const same = n0(c.match_same);
      const related = n0(c.match_related);
      const uncertain = n0(c.match_uncertain);
      const different = n0(c.match_different);
      return {
        input: same + related + uncertain + different,
        output: same,
        fates: [
          { id: 'same', value: same },
          { id: 'related', value: related },
          { id: 'uncertain', value: uncertain },
          { id: 'different', value: different },
        ],
      };
    },
    fates: {
      same: {
        label: 'همان است', kind: 'pass',
        why: 'مطمئن بود که این دو یک رویدادند. در مرحله‌ی بعد در یک خوشه می‌نشینند.',
      },
      related: {
        label: 'مرتبط', kind: 'info',
        why: 'یکی نیستند ولی به هم ربط دارند — مثلِ دو سانسِ یک نمایش، یا دو '
          + 'روزِ یک جشنواره.',
      },
      uncertain: {
        label: 'نامطمئن', kind: 'hold',
        why: 'نتوانست بگوید یکی‌اند یا نه. **ریزش نیست، ولی مهم‌ترین عددِ کیفیت '
          + 'است**: هر کدامش می‌تواند یک رویدادِ تکراری در اپ بشود.',
        like: 'مثلِ دو اعلان با همان تاریخ و همان سالن ولی دو عنوانِ کمی متفاوت.',
      },
      different: {
        label: 'فرق دارند', kind: 'info',
        why: 'دو رویدادِ جدا. جوابِ عادی و پرتکرارِ این مرحله — بیشترِ جفت‌ها همینند.',
      },
    },
  },
  {
    id: 'cluster',
    stages: ['vnext_cluster', 'cluster'],
    title: 'خوشه‌بندی',
    unit: 'یک mention',
    what: 'mentionهایی که «همان است» گرفتند در یک خوشه جمع می‌شوند. هر خوشه '
      + 'یعنی **یک رویدادِ یکتا**.',
    like: 'مثلِ گذاشتنِ همه‌ی یادداشت‌های مربوط به یک کنسرت در یک پوشه. تعدادِ '
      + 'پوشه‌ها یعنی تعدادِ رویدادهای واقعی.',
    inputLabel: 'mention',
    outputLabel: 'خوشه (رویدادِ یکتا)',
    unitShift: 'خروجی از ورودی کمتر است و این دقیقاً کارِ این مرحله است: چند '
      + 'mention یک خوشه می‌شوند. ⚠ شمارشِ خوشه به **نسلِ** خوشه‌بندی بند است، '
      + 'نه به پنجره‌ی زمانی؛ پس دو ستونش یا برابرِ کل‌اند یا صفر.',
    read: (c) => {
      const mention = n0(c.mentions);
      const cluster = Math.min(n0(c.clusters), mention);
      return {
        input: mention,
        output: n0(c.clusters),
        fates: [
          { id: 'own', value: cluster },
          { id: 'merged', value: Math.max(mention - cluster, 0) },
        ],
      };
    },
    fates: {
      own: {
        label: 'خوشه‌ی خودش را ساخت', kind: 'pass',
        why: 'اولین باری که این رویداد دیده شده، پس خوشه‌ی تازه‌ای باز کرد.',
      },
      merged: {
        label: 'با یکی دیگر یکی شد', kind: 'info',
        why: '**ریزش نیست** — همان رویداد است که از منبعِ دوم هم دیده شده و به '
          + 'خوشه‌ی موجود اضافه شد. هر چه این عدد بیشتر، پوششِ ما بهتر.',
      },
    },
  },
  {
    id: 'merge',
    stages: ['vnext_merge', 'merge'],
    title: 'یکی کردنِ محتوا',
    unit: 'یک خوشه',
    what: 'از چند mentionِ یک خوشه، یک نسخه‌ی نهایی ساخته می‌شود — کدام عنوان، '
      + 'کدام تاریخ، کدام عکس.',
    like: 'مثلِ باز کردنِ پوشه و نوشتنِ یک کارتِ تمیز از روی سه یادداشتی که '
      + 'تویش است، با برداشتنِ کامل‌ترین تکه‌ی هر کدام.',
    inputLabel: 'خوشه',
    outputLabel: 'خوشه‌ی چندمنبعی',
    read: (c) => {
      const cluster = n0(c.clusters);
      const multi = Math.min(n0(c.clusters_multi_source), cluster);
      return {
        input: cluster,
        output: multi,
        fates: [
          { id: 'multi', value: multi },
          { id: 'single', value: Math.max(cluster - multi, 0) },
        ],
      };
    },
    fates: {
      multi: {
        label: 'از چند منبع', kind: 'pass',
        why: 'این رویداد را بیش از یک سایت گفته. **بهترین حالت**: هم اعتمادِ '
          + 'بیشتر، هم فیلدهای کامل‌تر، چون کمبودِ هر منبع را دیگری پر می‌کند.',
      },
      single: {
        label: 'فقط یک منبع', kind: 'info',
        why: 'فقط یک سایت این رویداد را گفته. ایرادی ندارد ولی هر فیلدِ غلطش '
          + 'همان‌طور می‌ماند — چیزی برای مقایسه نیست.',
      },
    },
  },
  {
    id: 'policy',
    stages: ['vnext_publish_policy', 'policy'],
    title: 'حکمِ انتشار',
    unit: 'یک خوشه',
    what: 'برای هر رویدادِ یکتا تصمیم گرفته می‌شود: برود روی اپ، برود به صفِ '
      + 'بازبینی، یا اصلاً نرود. «نه» چهار حالت دارد و هر چهارتا از جنسِ '
      + '«چیزی که لازم است نداریم» هستند: تاریخ ندارد، عنوان ندارد، دو منبع '
      + 'دو چیزِ متفاوت می‌گویند، یا هیچ منبعِ معتبری تأییدش نکرده. جدولِ '
      + 'پایینِ همین مرحله می‌گوید کدام دلیل چقدر پیش آمده.',
    like: 'مثلِ نگاه کردن به کارتِ تمیزِ هر رویداد و تصمیم گرفتن که روی تابلوی '
      + 'خودمان بچسبانیمش، بگذاریمش کنار تا کسی نگاهش کند، یا دور بیندازیمش.',
    inputLabel: 'خوشه‌ی حکم‌خورده',
    outputLabel: 'حکمِ «منتشر شود»',
    read: (c) => {
      const publish = n0(c.policy_publish);
      const review = n0(c.policy_review);
      const reject = n0(c.policy_reject);
      return {
        input: publish + review + reject,
        output: publish,
        fates: [
          { id: 'publish', value: publish },
          { id: 'review', value: review },
          { id: 'reject', value: reject },
        ],
      };
    },
    fates: {
      publish: {
        label: 'منتشر شود', kind: 'pass',
        why: 'سیاستِ انتشار گفت «آری». یعنی «می‌تواند برود»، نه «رفته» — بردنش '
          + 'کارِ مرحله‌ی بعد است.',
      },
      review: {
        label: 'بازبینی', kind: 'hold',
        why: 'نه «آری» گفت نه «نه» — یعنی چیزی هست که ماشین نباید خودش درباره‌اش '
          + 'تصمیم بگیرد: دو منبع دو مقدارِ متفاوت داده‌اند، یا هیچ منبعِ معتبری '
          + 'تأییدش نکرده. تا وقتی این‌جاست به اپ نمی‌رود؛ سربرگِ «صفِ بازبینی» '
          + 'دقیقاً همین‌هاست و یک آدم باید تصمیم بگیرد.',
      },
      reject: {
        label: 'رد شد', kind: 'drop',
        why: 'سیاست ردش کرد، و تقریباً همیشه به یک دلیل: **چیزی که بدونش '
          + 'انتشار بی‌معنی است نداریم** — تاریخِ شروع، یا عنوان. جدولِ پایینِ '
          + 'همین مرحله سهمِ هر دلیل را می‌گوید.',
      },
    },
  },
  {
    id: 'publish',
    stages: ['publish', 'vnext_publish', 'publisher'],
    title: 'رساندن به اپ',
    unit: 'یک حکمِ «منتشر شود»',
    what: 'رویدادهایی که حکمِ انتشار گرفتند به `public.events` نوشته می‌شوند تا '
      + 'کاربر ببیندشان.',
    like: 'مثلِ چسباندنِ کارتِ نهایی روی تابلوی خودمان. تا این‌جا هیچ‌کس بیرون '
      + 'از این ساختمان چیزی ندیده.',
    inputLabel: 'حکمِ «منتشر شود»',
    outputLabel: 'روی اپ',
    read: (c) => {
      const publish = n0(c.policy_publish);
      const mapped = Math.min(n0(c.publishable_mapped), publish);
      const rest = Math.max(publish - mapped, 0);
      // ⚠ **تفکیکی که یک دروغِ نیم‌بند را تعمیر می‌کند (۰۱۴۴).** «نرفته» از
      // تفریق می‌آمد، و `publishable_mapped` به **دفترِ خودِ کرالر** نگاه
      // می‌کند. روی داده‌ی امروز از ۲٬۰۳۴ تای «نرفته»، ۱٬۸۳۶ تا در اپ هستند
      // و کاربر می‌بیندشان؛ فقط کرالر ردیفِ دفترش را ننوشته. یکی نشان
      // دادنشان یعنی فرستادنِ آدم دنبالِ دو هزار رویدادِ گم‌شده که ۹۰
      // درصدشان گم نشده‌اند.
      const inApp = Math.min(n0(c.notsent_in_app), rest);
      const gone = c.notsent_gone === undefined
        ? 0
        : Math.min(n0(c.notsent_gone), rest - inApp);
      return {
        input: publish,
        output: mapped,
        fates: [
          { id: 'inapp', value: mapped },
          { id: 'notsent_in_app', value: inApp },
          { id: 'notsent_gone', value: gone },
          { id: 'notsent', value: Math.max(rest - inApp - gone, 0) },
          { id: 'no_date', value: n0(c.events_no_date) },
          { id: 'no_url', value: n0(c.events_no_url) },
          { id: 'dead_cluster', value: n0(c.events_dead_cluster) },
          { id: 'orphaned', value: n0(c.orphaned) },
        ],
      };
    },
    fates: {
      inapp: {
        label: 'روی اپ', kind: 'pass',
        why: 'به `public.events` رسیده و کاربر می‌تواند ببیندش. تهِ خط.',
      },
      notsent_in_app: {
        label: 'در اپ هست، فقط ثبت نشده', kind: 'info',
        why: 'کاربر این رویداد را در اپ **می‌بیند** — ردیفش در `public.events` '
          + 'هست. چیزی که نیست، ردیفِ دفترِ خودِ کرالر است. یعنی این‌ها گم '
          + 'نشده‌اند و دنبالشان گشتن وقت تلف کردن است؛ ایراد در بایگانیِ '
          + 'کرالر است، نه در اپ.',
        like: 'مثلِ کاغذی که روی تابلو چسبانده‌ای ولی یادت رفته در دفترچه‌ات '
          + 'بنویسی که چسبانده‌ای.',
      },
      notsent_gone: {
        label: 'واقعاً نرفته', kind: 'hold',
        why: 'نه ردیفی در اپ دارد نه شناسه‌ای گرفته. **این‌ها را کاربر '
          + 'نمی‌بیند.** جدولِ پایینِ همین مرحله نمونه‌هایشان را با لینکِ سایتِ '
          + 'منبع می‌آورد تا بشود همان‌جا باز کرد و دید رویداد واقعاً هست یا نه.',
      },
      notsent: {
        label: 'نرفته (بی‌تفکیک)', kind: 'hold',
        why: 'حکمش «منتشر شود» است ولی ردیفی در دفترِ کرالر ندارد، و این '
          + 'گزارش نمی‌داند در اپ هست یا نه. ⚠ عددِ بزرگ این‌جا یعنی کش هنوز '
          + 'کلیدهای مایگریشنِ ۰۱۴۴ را ندارد و تفکیک یک اجرای کرالر دیرتر می‌آید.',
      },
      no_date: {
        label: 'بدونِ تاریخ', kind: 'drop', aside: true,
        why: 'رویدادی که تاریخ ندارد منتشر نمی‌شود. معمولاً یعنی استخراجِ تاریخ '
          + 'برای آن منبع کار نکرده.',
      },
      no_url: {
        label: 'بدونِ آدرس', kind: 'drop', aside: true,
        why: 'بدونِ لینک هم منتشر نمی‌شود — کاربر جایی برای رفتن ندارد.',
      },
      dead_cluster: {
        label: 'خوشه‌ی مرده', kind: 'drop', aside: true,
        why: 'ردیفِ نهایی دارد ولی خوشه‌اش در آخرین نسلِ خوشه‌بندی نیست. یعنی '
          + 'یک رویدادِ جامانده از نسلِ قبل.',
      },
      orphaned: {
        label: 'بی‌صاحب', kind: 'drop', aside: true,
        why: 'در اپ ردیف دارد ولی دیگر هیچ mentionِ زنده‌ای پشتش نیست — منبعش '
          + 'برش داشته و ما هنوز نگهش داشته‌ایم.',
      },
    },
  },
];

/**
 * برچسب و توضیحِ یک دسته، از روی شناسه‌اش.
 *
 * ⚠ جدولِ نمونه‌ها (مایگریشنِ ۰۱۴۵) دسته‌هایی را هم برمی‌گرداند که در
 * `StageRow.fates` نیستند — چیپِ یک دسته وقتی عددش در **هر دو** ستون صفر
 * باشد کشیده نمی‌شود، ولی نمونه‌اش ممکن است باشد (پنجره‌ی نمونه و پنجره‌ی
 * شمارش یکی نیستند: رجیستری و خوشه و کشف اصلاً پنجره‌ی زمانی ندارند).
 *
 * پس رابط نمی‌تواند برچسب را فقط از `fates` بردارد و این تابع لازم است.
 * `null` یعنی شناسه‌ای آمده که این فایل نمی‌شناسدش — رابط باید خودِ شناسه
 * را خام نشان بدهد و نه «نامعلوم».
 */
export function fateMeta(
  stageId: string, bucketId: string,
): { label: string; kind: FateKind; why: string; like?: string } | null {
  const def = DEFS.find((d) => d.id === stageId);
  const m = def?.fates[bucketId];
  return m ? { label: m.label, kind: m.kind, why: m.why, like: m.like } : null;
}

/** ثانیه از `Seconds`، با گارد برای وقتی کلید اصلاً نیامده. */
const sec = (s: { d10: number | null; run: number | null } | null | undefined): number | null =>
  (s && typeof s.d10 === 'number' ? s.d10 : null);

function health(r: StageRun): StageHealth {
  const p50 = sec(r.p50_seconds);
  const p95 = sec(r.p95_seconds);
  return {
    runs: pair(n0(r.runs?.d10), n0(r.runs?.run)),
    failures: pair(n0(r.failures?.d10), n0(r.failures?.run)),
    failRate: n0(r.runs?.d10) > 0
      ? Math.round((n0(r.failures?.d10) / n0(r.runs?.d10)) * 1000) / 10
      : null,
    p50,
    p95,
    slow: p50 != null && p95 != null && p50 > 0 && p95 > p50 * 5,
    lastRun: r.last_run ?? null,
    lastOk: r.last_ok ?? null,
    lastError: r.last_error ?? null,
    errorFamily: classifyError(r.last_error),
  };
}

/**
 * ده مرحله، با عدد.
 *
 * `run` می‌تواند `null` باشد (هیچ اجرای کاملی ثبت نشده)؛ آن‌وقت عددِ دومِ
 * هر جا صفر است، نه اینکه ستون حذف شود — ستونی که گاهی هست و گاهی نیست
 * چیدمان را هر بار جابه‌جا می‌کند.
 */
/**
 * جدول‌های «چرا» را می‌سازد و به مرحله‌ی خودش می‌چسباند.
 *
 * ⚠ همه‌شان از پنجره‌ی **ده روز** می‌آیند و ستونِ «آخرین اجرا» ندارند — این
 * در متنِ خودِ هر پنل نوشته می‌شود، وگرنه آدم دنبالِ عددِ دومی می‌گردد که
 * وجود ندارد.
 */
function panelsFor(id: string, x: StageExtras): Panel[] {
  const share = (n: number, total: number) =>
    (total > 0 ? Math.round((n / total) * 1000) / 10 : null);

  if (id === 'fetch' && x.fetch_repeat?.length) {
    return [{
      kind: 'repeat',
      title: 'همان آدرس، چند بار؟',
      note: 'کشِ خطا جلوی **تماسِ شبکه** را می‌گیرد، نه جلوی وارد شدنِ آدرس به '
        + 'صف. پس هر آدرسِ مرده هر شب دوباره سرِ صف می‌آید و دوباره رد می‌شود — '
        + 'بی‌هزینه، ولی شمرده می‌شود. ستونِ «آدرسِ یکتا» می‌گوید واقعاً با چند '
        + 'چیزِ متفاوت طرفیم. همه از ۱۰ روزِ اخیر.',
      rows: x.fetch_repeat.map((r) => ({
        bucket: r.bucket,
        requests: n0(r.requests),
        urls: n0(r.urls),
        sources: n0(r.sources),
        perUrl: n0(r.urls) > 0
          ? Math.round((n0(r.requests) / n0(r.urls)) * 10) / 10
          : null,
      })).sort((a, b) => b.requests - a.requests),
    }];
  }

  if (id === 'promote' && x.drafts_reasons?.length) {
    const total = x.drafts_reasons.reduce((s, r) => s + n0(r.n), 0);
    return [{
      kind: 'reasons',
      title: 'چرا ترفیع نگرفت',
      note: 'رشته‌ها را خودِ کرالر نوشته. همه از ۱۰ روزِ اخیر.',
      rows: x.drafts_reasons.map((r) => ({
        key: r.reason, ...reasonText(r.reason), n: n0(r.n), share: share(n0(r.n), total),
      })),
    }];
  }

  if (id === 'policy' && x.policy_reasons?.length) {
    const total = x.policy_reasons.reduce((s, r) => s + n0(r.n), 0);
    return [{
      kind: 'reasons',
      title: 'در چه شرایطی منتشر نمی‌شود',
      note: 'یک رویداد می‌تواند بیش از یک دلیل داشته باشد، پس جمعِ این ستون از '
        + 'شمارِ رویدادها بیشتر می‌شود. حکمِ «منتشر شود» دلیل ندارد و نباید هم '
        + 'داشته باشد: «آری» توضیح نمی‌خواهد. همه از ۱۰ روزِ اخیر.',
      rows: x.policy_reasons.map((r) => ({
        key: r.reason,
        ...reasonText(r.reason),
        label: `${reasonText(r.reason).label} · ${r.decision === 'review' ? 'بازبینی' : 'رد'}`,
        n: n0(r.n),
        share: share(n0(r.n), total),
      })),
    }];
  }

  if (id === 'publish' && x.notsent_rows?.length) {
    return [{
      kind: 'notsent',
      title: 'نمونه‌ی آن‌هایی که نرفته‌اند',
      note: 'لینکِ هر ردیف، صفحه‌ی خودِ رویداد در سایتِ منبع است — همان‌جا باز '
        + 'کنید و ببینید واقعاً هست یا نه. بیست ردیفِ آخر.',
      rows: x.notsent_rows.map((r) => ({
        title: r.title,
        url: r.url,
        source: r.source_id,
        why: r.why,
        whyLabel: NOTSENT_WHY[r.why]?.label ?? r.why,
        at: r.at,
      })),
    }];
  }

  return [];
}

export function stageReport(
  d10: StageCounts,
  run: StageCounts | null,
  stages: StageRun[],
  extras: StageExtras = {},
): StageReport {
  const byStage = new Map<string, StageRun>();
  for (const s of stages) byStage.set(String(s.stage ?? '').toLowerCase(), s);

  const used = new Set<string>();

  const rows = DEFS.map((def): StageRow => {
    const a = def.read(d10);
    const b = def.read(run ?? {});

    const bVal = new Map(b.fates.map((x) => [x.id, x.value]));

    const fates: Fate[] = a.fates
      // گرهِ صفر کشیده نمی‌شود، **مگر** اینکه در آخرین اجرا غیرصفر باشد:
      // حذفش دقیقاً همان چیزی را پنهان می‌کند که تازه پیدا شده.
      .filter((x) => x.value > 0 || (bVal.get(x.id) ?? 0) > 0)
      .map((x) => {
        const meta = def.fates[x.id]!;
        return {
          id: x.id,
          label: meta.label,
          kind: meta.kind,
          v: pair(x.value, bVal.get(x.id) ?? 0),
          why: meta.why,
          like: meta.like,
          aside: meta.aside,
          share: a.input > 0 && !meta.aside
            ? Math.round((x.value / a.input) * 1000) / 10
            : null,
        };
      });

    const counted = a.fates
      .filter((x) => !def.fates[x.id]!.aside)
      .reduce((s, x) => s + x.value, 0);

    let found: StageRun | undefined;
    for (const name of def.stages) {
      const hit = byStage.get(name);
      if (hit) { found = hit; used.add(name); break; }
    }

    return {
      id: def.id,
      // ⚠ نامی که **واقعاً** پیدا شد و نه نامِ اولِ فهرست: رابط این رشته را
      // کنارِ عنوانِ فارسی چاپ می‌کند و آدم با همین در `crawler.runs` دنبالش
      // می‌گردد. نوشتنِ `merge` وقتی جدول `vnext_merge` دارد یعنی فرستادنِ
      // او به دنبالِ ردیفی که وجود ندارد.
      stage: found?.stage ?? def.stages[0]!,
      title: def.title,
      unit: def.unit,
      what: def.what,
      like: def.like,
      input: { label: def.inputLabel, v: pair(a.input, b.input) },
      output: { label: def.outputLabel, v: pair(a.output, b.output) },
      unitShift: def.unitShift ?? null,
      fates,
      // مرحله‌ی بی‌سرنوشت ورودی‌اش را تقسیم نمی‌کند، پس «حساب‌نشده» هم ندارد.
      residual: a.fates.length ? Math.max(a.input - counted, 0) : 0,
      panels: panelsFor(def.id, extras),
      health: found ? health(found) : null,
      idle: !found || n0(found.runs?.d10) === 0,
    };
  });

  return {
    rows,
    unknown: stages.filter((s) => !used.has(String(s.stage ?? '').toLowerCase())),
  };
}
